import { ColumnDef } from "@tanstack/react-table";
import React, { useCallback, useMemo } from "react";
import { DOC_TYPE_ID, Visitor } from "../../../constants/common";
import { Button } from "../../button";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";

interface VisitorsGroupTableProps {
  addedVisitors: Visitor[];
  setAddedVisitors: React.Dispatch<React.SetStateAction<Visitor[]>>;
  setEditingVisitor: React.Dispatch<React.SetStateAction<Visitor | null>>;
  setIsAddVisitorFormVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onFormReset?: () => void;
}

const VisitorsGroupTable = ({
  addedVisitors,
  setAddedVisitors,
  setEditingVisitor,
  setIsAddVisitorFormVisible,
  onFormReset,
}: VisitorsGroupTableProps) => {
  const { t } = useTranslation();

  const handleEdit = useCallback(
    (visitor: Visitor) => {
      setEditingVisitor(visitor);
      setIsAddVisitorFormVisible(true);
    },
    [setEditingVisitor, setIsAddVisitorFormVisible]
  );

  const handleDelete = useCallback(
    (id: string) => {
      setAddedVisitors((prev) => prev.filter((visitor) => visitor.id !== id));
      setEditingVisitor(null);

      // Reset the form when a visitor is deleted
      if (onFormReset) {
        onFormReset();
      }
    },
    [onFormReset, setAddedVisitors, setEditingVisitor]
  );

  const visitorsColumns: ColumnDef<Visitor>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: t("visitorName"),
        cell: ({ row }) => {
          return <span>{row.original.FullName}</span>;
        },
      },
      {
        accessorKey: "companyName",
        header: t("companyName"),
        cell: ({ row }) => {
          return <span>{row.original.Sponsor}</span>;
        },
      },
      {
        accessorKey: "email",
        header: t("email"),
        cell: ({ row }) => {
          return <span>{row.original.Email}</span>;
        },
      },
      {
        accessorKey: "number",
        header: t("mobileNumber"),
        cell: ({ row }) => {
          return <span>{row.original.MobileNo}</span>;
        },
      },
      {
        accessorKey: "IDType",
        header: t("idType"),
        cell: ({ row }) => {
          return <span>{row.original.IDType}</span>;
        },
      },
      {
        accessorKey: "eid",
        header: t("idNumber"),
        cell: ({ row }) => {
          return (
            <span>
              {row.original.IDType === DOC_TYPE_ID.NATIONAL_ID
                ? row.original.EIDNumber
                : row.original.IDType === DOC_TYPE_ID.PASSPORT
                ? row.original.PassportNumber
                : row.original.IDType === DOC_TYPE_ID.IQAMA
                ? row.original.S_Iqama
                : row.original.IDType === DOC_TYPE_ID.VISA
                ? row.original.S_VisaNo
                : row.original.S_OthersValue}
            </span>
          );
        },
      },
      {
        accessorKey: "eidExpiryDate",
        header: t("idExpiryDate"),
        cell: ({ row }) => {
          return (
            <span>
              {row.original.IDType === DOC_TYPE_ID.NATIONAL_ID
                ? row.original.Dt_EIDExpiryDate
                  ? new Date(row.original.Dt_EIDExpiryDate).toLocaleDateString()
                  : ""
                : row.original.IDType === DOC_TYPE_ID.PASSPORT
                ? row.original.Dt_PassportExpiryDate
                  ? new Date(
                      row.original.Dt_PassportExpiryDate
                    ).toLocaleDateString()
                  : ""
                : row.original.IDType === DOC_TYPE_ID.IQAMA
                ? row.original.Dt_IqamaExpiry
                  ? new Date(row.original.Dt_IqamaExpiry).toLocaleDateString()
                  : ""
                : row.original.IDType === DOC_TYPE_ID.VISA
                ? row.original.Dt_VisaExpiryDate
                  ? new Date(
                      row.original.Dt_VisaExpiryDate
                    ).toLocaleDateString()
                  : ""
                : row.original.Dt_OthersExpiry
                ? new Date(row.original.Dt_OthersExpiry).toLocaleDateString()
                : ""}
            </span>
          );
        },
      },
      {
        accessorKey: "nationality",
        header: t("nationality"),
        cell: ({ row }) => {
          return <span>{row.original.Nationality}</span>;
        },
      },
      // {
      //   accessorKey: "S_VehicleNo",
      //   header: t("vehicleNo"),
      //   cell: ({ row }) => {
      //     return <span>{row.original.S_VehicleNo}</span>;
      //   },
      // },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <div className="flex gap-1 items-center">
              <Button
                type="button"
                onClick={() => handleEdit(row.original)}
                className="py-0.5"
              >
                {t("edit")}
              </Button>
              <Button
                onClick={() => handleDelete(row.original.id)}
                className="bg-red-600 hover:bg-red-500 py-0.5"
              >
                {t("delete")}
              </Button>
            </div>
          );
        },
      },
    ],
    [t, handleEdit, handleDelete]
  );

  return (
    <TableWrapper
      data={addedVisitors}
      columns={visitorsColumns}
      isPagination={false}
      isColumnChooser={false}
    />
  );
};

export default React.memo(VisitorsGroupTable);
